<template>
  <a-layout class="login-container">
    <a-layout-content>
      <a-card title="用户登录" class="login-card">
        <template #extra>
          <a-button
              type="link"
              @click="toSignup()"
          >
            去注册
          </a-button>
        </template>
        <a-tabs v-model:activeKey="activeMethod" centered v-if="loginMethods.length > 1">
          <a-tab-pane v-for="method in loginMethods" :key="method.key" :tab="method.name"/>
        </a-tabs>
        <a-form
            :model="formState"
            name="login"
            :label-col="{ span: 6 }"
            :wrapper-col="{ span: 16 }"
            @finish="onFinish"
            @finishFailed="onFinishFailed"
            v-if="activeMethod === 'password'"
        >
          <!-- 账号输入 -->
          <a-form-item
              label="账号"
              name="username"
              :rules="[{ required: true, message: '请输入账号' }]"
          >
            <a-input
                v-model:value="formState.username"
                placeholder="请输入账号"
            />
          </a-form-item>

          <!-- 密码输入 -->
          <a-form-item
              label="密码"
              name="password"
              :rules="[{ required: true, message: '请输入密码' }]"
          >
            <a-input-password
                v-model:value="formState.password"
                placeholder="请输入密码"
            />
          </a-form-item>

          <!-- 验证码 -->
          <a-form-item
              v-if="captchaScene.switch"
              v-show="captchaProviderId > 0"
              label="验证码"
              name="captcha_answer"
              :rules="captchaProviderId > 0 ? [{ required: true, message: '请输入验证码！' }] : []"
          >
            <Captcha
                v-model:value="formState.captcha_answer"
                scene="login"
                ref="captchaRef"
                @provider-id-updated="handleCaptchaProviderIdUpdated"
            />
          </a-form-item>

          <a-form-item :wrapper-col="{ offset: 6, span: 16 }">
            <a-button type="primary" html-type="submit" :loading="loading">登录</a-button>
            <a-button style="margin-left: 10px" @click="resetForm">重置</a-button>
          </a-form-item>
        </a-form>

        <!-- 第三方登录方式 -->
        <div class="third-login-container" v-if="login3rdMethods.length > 0">
          <div class="third-login-title">第三方登录</div>
          <div class="third-login-methods">
            <a-button
                v-for="method in login3rdMethods"
                :key="method.key"
                type="text"
                @click="handle3rdLogin(method)"
                class="third-login-btn"
            >
              <img :src="method.logo" :alt="method.name" class="third-login-logo">
            </a-button>
          </div>
        </div>
        <template v-if="scene === 'pc'">
          <!-- 登录二维码弹窗 -->
          <PcWechatOpenPlatformLogin v-if="active3rdMethod.type === 'wechat_open_platform'" :method="active3rdMethod"></PcWechatOpenPlatformLogin>
          <PcWechatOfficialAccountLogin v-else-if="active3rdMethod.type === 'wechat_official_account'" :method="active3rdMethod"></PcWechatOfficialAccountLogin>
        </template>
        <template v-else-if="scene === 'wechat_h5'">
          <WechatH5WechatOfficialAccountLogin v-if="active3rdMethod.type === 'wechat_official_account'" :method="active3rdMethod"></WechatH5WechatOfficialAccountLogin>
        </template>
      </a-card>
    </a-layout-content>
  </a-layout>
</template>

<script>
import {defineComponent, onMounted, reactive, ref} from 'vue'
import {useStore} from 'vuex';
import {useRoute, useRouter} from 'vue-router';
import {message} from 'ant-design-vue'
import request from '@/utils/request'
import Captcha from "@/components/captcha.vue";
import PcWechatOpenPlatformLogin from "../components/pc_wechat_open_platform.vue";
import PcWechatOfficialAccountLogin from "../components/pc_wechat_official_account.vue";
import WechatH5WechatOfficialAccountLogin from "../components/wechat_h5_wechat_official_account.vue";
import { isWeChatBrowser } from '@/utils/util.js';
import { setLoginFrom, pushLoginFrom } from '@/storage/login_from'

export default defineComponent({
  components: {Captcha, PcWechatOpenPlatformLogin, PcWechatOfficialAccountLogin, WechatH5WechatOfficialAccountLogin},
  setup() {
    const store = useStore();
    const router = useRouter();
    const route = useRoute();

    const loading = ref(false)
    const scene = ref('pc')
    const loginMethods = ref([])
    const activeMethod = ref('password') // 默认账号密码登录
    const login3rdMethods = ref([])
    const active3rdMethod = ref(
        {
          type: '',
          key: '',
          name: '',
          logo: '',
        }
    )

    const captchaRef = ref(null)
    const captchaScene = ref({switch: false})
    const captchaProviderId = ref(0)

    const formState = reactive({
      username: '',
      password: '',
      captcha_answer: '',
      captcha_key: '',
      provider_id: 0
    })

    if (isWeChatBrowser()) {
      scene.value = 'wechat_h5'
    }

    if (route.query.from) {
      setLoginFrom(route.query.from)
    }

    // 获取登录场景配置
    const fetchLoginScene = async () => {
      try {
        let url = ''
        if (scene.value === 'pc') {
          url = '/site/scene/login/pc'
        } else if (scene.value === 'wechat_h5') {
          url = '/site/scene/login/wechat_h5'
        }
        const response = await request.get(url)
        const data = response.data
        captchaScene.value = data.captcha || {}
        loginMethods.value = data.login_methods || []
        login3rdMethods.value = data.login_3rd_methods || []

        // 设置默认登录方式
        if (loginMethods.value.length > 0) {
          activeMethod.value = loginMethods.value[0].key
        }
      } catch (error) {
        message.error('获取登录配置失败')
        console.error(error)
      }
    }

    // 处理captchaProviderId更新，用于判断验证码是否有效
    const handleCaptchaProviderIdUpdated = (providerId) => {
      captchaProviderId.value = providerId;
    }

    // 提交登录
    const onFinish = async (values) => {
      loading.value = true
      const captchaKey = captchaRef.value?.captchaKey;
      const payload = {
        ...values,
        captcha_key: captchaKey,
        provider_id: captchaProviderId.value,
      }

      store.dispatch('Login', payload)
          .then((res) => {
            if (res.code !== 'success') {
              message.error(res.msg)
              // 登录失败时刷新验证码
              captchaRef.value.refreshCaptcha()
              return
            }
            const loginFrom = pushLoginFrom()
            if (loginFrom) {
              window.location.href = loginFrom
            } else {
              router.push({path: '/'})
            }
          })
          .catch(err => {
            message.error('请求出现错误，请稍后再试');
            console.log(err);
            // 登录失败时刷新验证码
            captchaRef.value.refreshCaptcha();
          })
          .finally(() => {
            loading.value = false;
          });
    }

    const onFinishFailed = (errorInfo) => {
      console.log('Failed:', errorInfo)
    }

    const resetForm = () => {
      Object.keys(formState).forEach(key => {
        formState[key] = ''
      })
    }

    const handle3rdLogin = async (method) => {
      active3rdMethod.value = method
    }

    const toSignup = () => {
      router.push({path: '/auth/signup'});
    }

    onMounted(() => {
      fetchLoginScene()
    })

    return {
      loading,
      loginMethods,
      activeMethod,
      formState,
      onFinish,
      onFinishFailed,
      resetForm,
      captchaRef,
      captchaScene,
      captchaProviderId,
      handleCaptchaProviderIdUpdated,
      toSignup,
      scene,
      login3rdMethods,
      active3rdMethod,
      handle3rdLogin,
    }
  }
})
</script>

<style scoped>
.login-container {
  min-height: 100vh;
  padding: 20px;
  background: #f0f2f5;
}

.login-card {
  max-width: 600px;
  margin: 0 auto;
}

.third-login-container {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #f0f0f0;
}

.third-login-title {
  text-align: center;
  margin-bottom: 15px;
  color: #888;
}

.third-login-methods {
  display: flex;
  justify-content: center;
  gap: 10px;
}

.third-login-btn {
  padding: 0;
  border: none;
}

.third-login-logo {
  width: 40px;
  height: 40px;
  object-fit: contain;
}
</style>
